import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [activeSlug, setActiveSlug] = useState(categorySlug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (categorySlug) {
      setActiveSlug(categorySlug);
      window.scrollTo(0, 0);
    }
  }, [categorySlug]);

  const activeCategory = categories.find(c => c.slug === activeSlug) || categories[0];

  const filteredComics: Comic[] = useMemo(() => {
    let result = activeSlug === 'all' ? [...comics] : comics.filter(c => c.categorySlug === activeSlug);

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    return result;
  }, [activeSlug, sortBy]);

  const formatViews = (views: number) => {
    if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
    if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
    return views.toLocaleString();
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.freekcomic.com/' },
      { '@type': 'ListItem', position: 2, name: `${activeCategory.name}韩漫`, item: `https://www.freekcomic.com/category/${activeCategory.slug}` },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name}韩漫 - 免费韩漫大全`,
    description: `免费韩漫大全${activeCategory.name}分类，汇集最新最热门的${activeCategory.name}类韩国漫画，每日更新。`,
    url: `https://www.freekcomic.com/category/${activeCategory.slug}`,
    hasPart: filteredComics.map((comic) => ({
      '@type': 'CreativeWork',
      name: comic.title,
      url: `https://www.freekcomic.com/article/${comic.id}`,
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }} />
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-800 font-medium">{activeCategory.name}韩漫</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
              {activeCategory.name}韩漫
            </h1>
            <p className="text-sm md:text-base text-foreground-500 mb-8">
              {activeSlug === 'all'
                ? '浏览全部品类韩国漫画，最新最热的韩漫尽在免费韩漫大全'
                : `精选${activeCategory.name}类韩国漫画，每日更新最新章节，实时追更不间断`}
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500">排序：</span>
              {[
                { key: 'latest' as const, label: '最新更新' },
                { key: 'popular' as const, label: '最多阅读' },
                { key: 'rating' as const, label: '最高评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-secondary-100 border border-background-200/70'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              <span className="text-xs text-foreground-400 ml-auto">
                共 {filteredComics.length} 部作品
              </span>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-emotion-sad-line text-2xl text-secondary-500"></i>
                </div>
                <p className="text-foreground-600">该分类暂无漫画，请浏览其他分类</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6" data-product-shop="">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        title={`${comic.title} - 免费韩漫大全`}
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          comic.status === '连载中'
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-secondary-500 text-background-50'
                        }`}>
                          {comic.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <p className="text-xs text-background-100/80">
                          更新至第{comic.chapters}话
                        </p>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{comic.author}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                        {comic.rating}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}